class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindCartLinks();
    this.initStickyButton();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  /*
    Add-to-cart flows (product form, quick add, ...) publish cartUpdate with
    the /cart/add response, which contains the pre-rendered sections we
    requested. Re-render the drawer from them and auto-open it (same
    behaviour as the replaced Qikify slide cart with "auto open" enabled).
    Quantity changes made inside the drawer (source 'cart-items') are already
    rendered by CartItems — only sync the counters for those.
  */
  onCartUpdate(event) {
    if (!event) return;

    if (event.source !== 'cart-items' && event.cartData && event.cartData.sections && event.cartData.sections['cart-drawer']) {
      this.renderContents(event.cartData);
      return;
    }

    if (event.cartData && typeof event.cartData.item_count === 'number') {
      this.syncCounts(event.cartData.item_count);
    }
  }

  syncCounts(itemCount) {
    const label = itemCount < 100 ? `${itemCount}` : '99+';
    this.querySelectorAll('[data-scd-count]').forEach((badge) => (badge.textContent = label));
    this.classList.toggle('is-empty', itemCount === 0);
    const stickyButton = this.querySelector('[data-scd-sticky]');
    if (stickyButton) stickyButton.setAttribute('aria-label', `Cart (${itemCount} items)`);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /*
    Open the drawer from any cart link, exactly like the app did with its
    '[href$="/cart"]' selector. Links can opt out with data-scd-no-open.
  */
  bindCartLinks() {
    document.querySelectorAll('a[href$="/cart"]:not(#cart-icon-bubble):not([data-scd-no-open])').forEach((link) => {
      if (link.closest('cart-drawer') || link.dataset.scdBound) return;
      link.dataset.scdBound = 'true';
      link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.open(link);
        },
        { capture: true }
      );
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.positionStickyButton();
      if (this.hasAttribute('data-auto-open')) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* -------------------------------------------------------------------------
     Sticky floating cart button — mirrors the app button: fixed round button,
     positioned per device setting, optionally draggable with the position
     remembered in localStorage, snapping to the nearest horizontal edge.
  ------------------------------------------------------------------------- */
  initStickyButton() {
    if (this.stickyInitialized) return;
    this.stickyInitialized = true;

    this.stickyGap = 5;
    this.stickyStorageKeys = {
      desktop: { x: 'scd-btn-pos-x', y: 'scd-btn-pos-y' },
      mobile: { x: 'scd-btn-pos-mobile-x', y: 'scd-btn-pos-mobile-y' },
    };

    this.positionStickyButton();

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.positionStickyButton(), 150);
    });

    // Delegated so the button keeps working after section re-renders.
    this.addEventListener('pointerdown', (event) => {
      const button = event.target.closest('[data-scd-sticky]');
      if (!button) return;
      this.onStickyPointerDown(event, button);
    });
  }

  isMobileViewport() {
    return window.innerWidth < 768;
  }

  getStickyDefaultPosition(button) {
    const gap = this.stickyGap;
    const size = parseInt(this.dataset.stickySize, 10) || 50;
    const width = document.documentElement.clientWidth;
    const height = document.documentElement.clientHeight;
    const position = this.isMobileViewport()
      ? this.dataset.stickyPosMobile || 'bottom_center'
      : this.dataset.stickyPosDesktop || 'bottom_right';

    const coords = { x: gap, y: gap };
    switch (position) {
      case 'top_left':
        break;
      case 'top_center':
        coords.x = width / 2 - size / 2;
        break;
      case 'top_right':
        coords.x = width - size - gap;
        break;
      case 'middle_left':
        coords.y = height / 2 - size / 2;
        break;
      case 'middle_right':
        coords.y = height / 2 - size / 2;
        coords.x = width - size - gap;
        break;
      case 'bottom_left':
        coords.y = height - size - gap;
        break;
      case 'bottom_center':
        coords.x = width / 2 - size / 2;
        coords.y = height - size - gap;
        break;
      case 'bottom_right':
      default:
        coords.x = width - size - gap;
        coords.y = height - size - gap;
        break;
    }
    return coords;
  }

  clampStickyCoords(coords) {
    const gap = this.stickyGap;
    const size = parseInt(this.dataset.stickySize, 10) || 50;
    const maxX = document.documentElement.clientWidth - size - gap;
    const maxY = document.documentElement.clientHeight - size - gap;
    return {
      x: Math.min(Math.max(coords.x, gap), Math.max(maxX, gap)),
      y: Math.min(Math.max(coords.y, gap), Math.max(maxY, gap)),
    };
  }

  positionStickyButton() {
    const button = this.querySelector('[data-scd-sticky]');
    if (!button) return;

    let coords = null;
    if (this.hasAttribute('data-sticky-drag')) {
      const keys = this.isMobileViewport() ? this.stickyStorageKeys.mobile : this.stickyStorageKeys.desktop;
      try {
        const x = localStorage.getItem(keys.x);
        const y = localStorage.getItem(keys.y);
        if (x !== null && y !== null) coords = { x: parseFloat(x), y: parseFloat(y) };
      } catch (e) {
        coords = null;
      }
    }
    if (!coords) coords = this.getStickyDefaultPosition(button);
    coords = this.clampStickyCoords(coords);

    this.stickyCoords = coords;
    button.style.transform = `translate3d(${coords.x}px, ${coords.y}px, 0)`;
  }

  onStickyPointerDown(event, button) {
    const dragEnabled = this.hasAttribute('data-sticky-drag');
    const start = { x: event.clientX, y: event.clientY };
    const origin = { ...(this.stickyCoords || this.getStickyDefaultPosition(button)) };
    let dragging = false;

    const onMove = (moveEvent) => {
      if (!dragEnabled) return;
      const dx = moveEvent.clientX - start.x;
      const dy = moveEvent.clientY - start.y;
      if (!dragging && Math.hypot(dx, dy) < 6) return;
      dragging = true;
      button.setAttribute('data-is-dragging', 'true');
      const coords = this.clampStickyCoords({ x: origin.x + dx, y: origin.y + dy });
      this.stickyCoords = coords;
      button.style.transform = `translate3d(${coords.x}px, ${coords.y}px, 0)`;
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);

      if (dragging) {
        button.removeAttribute('data-is-dragging');
        // Stick to the nearest horizontal edge, like the app.
        const size = parseInt(this.dataset.stickySize, 10) || 50;
        const width = document.documentElement.clientWidth;
        const coords = { ...this.stickyCoords };
        coords.x = coords.x + size / 2 < width / 2 ? this.stickyGap : width - size - this.stickyGap;
        this.stickyCoords = this.clampStickyCoords(coords);
        button.style.transform = `translate3d(${this.stickyCoords.x}px, ${this.stickyCoords.y}px, 0)`;

        const keys = this.isMobileViewport() ? this.stickyStorageKeys.mobile : this.stickyStorageKeys.desktop;
        try {
          localStorage.setItem(keys.x, this.stickyCoords.x);
          localStorage.setItem(keys.y, this.stickyCoords.y);
        } catch (e) {
          /* storage unavailable */
        }
      } else {
        this.open(button);
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
